import React, { forwardRef } from "react";
import * as Primitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { useDensity } from "../../theme/ThemeContext";
import type { Density } from "../../theme/types";

const checkbox = cva(
  [
    "peer shrink-0 rounded-sm border border-border bg-surface-raised",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600/40 focus-visible:ring-offset-2",
    "data-[state=checked]:bg-accent-600 data-[state=checked]:border-accent-600",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ],
  {
    variants: {
      density: {
        comfortable: "h-5 w-5",
        compact: "h-4 w-4",
      },
    },
    defaultVariants: { density: "comfortable" },
  }
);

type CheckboxProps = React.ComponentPropsWithoutRef<typeof Primitive.Root> &
  Omit<VariantProps<typeof checkbox>, "density"> & { density?: Density };

export const Checkbox = forwardRef<React.ElementRef<typeof Primitive.Root>, CheckboxProps>(
  ({ density, className = "", ...props }, ref) => {
    const resolvedDensity = useDensity(density);
    return (
      <Primitive.Root ref={ref} className={cn(checkbox({ density: resolvedDensity }), className)} {...props}>
        <Primitive.Indicator className="flex items-center justify-center text-on-accent">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </Primitive.Indicator>
      </Primitive.Root>
    );
  }
);
Checkbox.displayName = "Checkbox";
